import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useReduxSelector } from "@hooks/useReduxSelector";
import { nothingData } from "@routes/IconeSvg";

import { Evaluation_Format, Evaluation_Type } from "@helpers/_constants";

import { convertFrom100 } from "../_helpers/evaluationConverter";
import CriteriaGroupItem from "./CriteriaGroupItem";
import QuestionItem from "./QuestionItem";

const DISPLAY_MAX_BY_FORMAT = Object.fromEntries(
  Object.entries(Evaluation_Format).map(([key, value]) => [
    value.value,
    value.displayedMaxNote || value.maxNote
  ])
);

export default function ObjectiveTable(props) {
  const {
    assignedEmployee,
    evaluationType,
    showQuestionCollapse,
    tableTitle,
    criteriaNotations,
    goToDetail,
    readOnly = false
  } = props;

  const { t: translate } = useTranslation("assesment.frequency");

  const criteriaGroups = useReduxSelector(
    ({ evaluation }) => evaluation.evaluationCriteriaGroup.list
  );

  const mappedCriteriaGroups = useMemo(() => {
    const filteredCriteriaGroups = criteriaGroups.filter(
      (cg) => cg.evaluationType === evaluationType
    );
    let output = [];

    for (let criteriaGroup of filteredCriteriaGroups) {
      let sum = 0;
      let maxSum = 0;

      const evaluationCriterias = criteriaGroup.EvaluationCriterias.map(
        (criteria) => {
          const notation = criteriaNotations.find(
            (cn) => cn.criteriaId === criteria._id
          );

          if (notation && notation.note !== null) {
            sum += convertFrom100(notation.note, criteria.evaluationFormat);
            maxSum += DISPLAY_MAX_BY_FORMAT[criteria.evaluationFormat];
          }

          return {
            ...criteria,
            notation
          };
        }
      );

      if (evaluationCriterias.some((criteria) => !!criteria.notation)) {
        output.push({
          ...criteriaGroup,
          sum,
          maxSum,
          EvaluationCriterias: evaluationCriterias
        });
      }
    }

    return output;
  }, [criteriaGroups, criteriaNotations]);

  const notationSum = useMemo(() => {
    let sum = 0;
    let maxSum = 0;

    for (let criteriaGroup of mappedCriteriaGroups) {
      maxSum += criteriaGroup.maxSum;
      sum += criteriaGroup.sum;
    }

    return `${sum}/${maxSum}`;
  }, [mappedCriteriaGroups]);

  return (
    <div className="arh-w-full arh-px-9 arh-pb-8">
      <div className="arh-mt-4">
        <table className="arh-mt-0 arh-w-full arh-table-fixed arh-items-center">
          <thead className="arh-sticky -arh-top-[1px] arh-z-[2]">
            <tr className="arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable">
              <th className="arh-w-[40%] arh-max-w-md arh-border-b arh-border-l-0 arh-border-r  arh-border-t-0 arh-border-solid arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-start arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {tableTitle}
              </th>
              <th className="arh-w-[25%] arh-max-w-md arh-border-b arh-border-l-0  arh-border-r arh-border-t-0 arh-border-solid arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-center arh-align-top arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Dernière évaluation")}
                <div>
                  <span className="arh-rounded-[55px] arh-bg-[#E6F8EF] arh-px-[10px] arh-text-[11px] arh-text-[#357569]">
                    {notationSum}
                  </span>
                </div>
              </th>
              <th className="arh-w-[25%] arh-max-w-md arh-border-b arh-border-l-0 arh-border-r arh-border-t-0  arh-border-solid arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-center arh-align-top arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Objectifs pour la prochaine évaluation")}
              </th>
              <th className="arh-w-[25%] arh-max-w-md arh-border-b arh-border-l-0 arh-border-r arh-border-t-0  arh-border-solid arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-center arh-align-top arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Moyens disponibles")}
              </th>
              <th className="arh-w-[25%] arh-max-w-md arh-border-b arh-border-l-0 arh-border-r arh-border-t-0  arh-border-solid arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-center arh-align-top arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Objectif général")}
              </th>
              <th className="arh-w-[12rem] arh-max-w-md arh-border-b arh-border-l-0 arh-border-r arh-border-t-0 arh-border-solid  arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-center arh-align-top arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Actions")}
              </th>
            </tr>
          </thead>

          <tbody>
            {mappedCriteriaGroups.map((criteriaGroup) => (
              <CriteriaGroupItem
                key={criteriaGroup._id}
                criteriaGroup={criteriaGroup}
                assignedEmployee={assignedEmployee}
                readOnly={readOnly}
                goToDetail={
                  goToDetail
                    ? (params) => goToDetail(params, evaluationType)
                    : null
                }
              />
            ))}
            {mappedCriteriaGroups.length !== 0 && (
              <QuestionItem
                assignedEmployee={assignedEmployee}
                showQuestionCollapse={showQuestionCollapse}
                evaluationType={evaluationType}
              />
            )}
          </tbody>
        </table>
        {mappedCriteriaGroups.length === 0 && (
          <div className="arh-full arh-my-14 arh-flex arh-flex-col arh-items-center arh-justify-center arh-font-poppins">
            {nothingData()}
            <p className="arh-m-5 arh-text-base arh-font-medium arh-text-[#949FB0]">
              {translate("Aucune donnée à afficher")}
            </p>
            <p className="arh-w-[32%] arh-text-center arh-text-base arh-font-medium arh-text-[#949FB0]">
              {translate(
                `Veuillez assigner une évaluation à cet employé afin d'afficher les compétences/évaluations correspondantes`,
                {
                  evaluationType:
                    evaluationType === Evaluation_Type.COMPETENCE
                      ? "compétences"
                      : "performances"
                }
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
